import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { AlertCircle, Lock } from "lucide-react";
import NDAModal from "../../components/private/NDAModal";
import { BRAND } from "../../data/content";
import { usePrivateAccessForm } from "./PrivateAccess/usePrivateAccessForm";
import AccessHeader from "./PrivateAccess/AccessHeader";
import AccessBackground from "./PrivateAccess/AccessBackground";
import AccessFormFields from "./PrivateAccess/AccessFormFields";
import NDAConsent from "./PrivateAccess/NDAConsent";

export default function PrivateAccess() {
    const navigate = useNavigate();
    const { form, errors, submitError, status, setField, onSubmit } =
        usePrivateAccessForm(navigate);
    const [ndaOpen, setNdaOpen] = useState(false);

    return (
        <div
            className="min-h-screen bg-[#0B0B0C] text-[#F3EFE7] relative overflow-hidden"
            data-testid="private-access-page"
        >
            <Helmet>
                <title>Private Portfolio — By Invitation · ACT</title>
                <meta name="robots" content="noindex,nofollow" />
                <meta
                    name="description"
                    content="Reserved for prospective clients and design partners. All visitors and content are governed by an NDA."
                />
            </Helmet>

            <AccessBackground />
            <AccessHeader />

            <main className="relative z-10 flex items-center justify-center px-6 py-14 md:py-20 min-h-[calc(100vh-82px)]">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full max-w-xl"
                >
                    <form
                        onSubmit={onSubmit}
                        noValidate
                        className="relative border border-[rgba(201,168,106,0.25)] bg-[#15161A]/85 backdrop-blur-md p-8 md:p-12 shadow-[0_40px_100px_rgba(0,0,0,0.55)]"
                        data-testid="private-access-form"
                    >
                        {/* Honeypot */}
                        <input
                            type="text"
                            name="website"
                            tabIndex="-1"
                            autoComplete="off"
                            value={form.website}
                            onChange={(e) => setField("website", e.target.value)}
                            aria-hidden="true"
                            style={{
                                position: "absolute",
                                left: "-9999px",
                                width: 1,
                                height: 1,
                                opacity: 0,
                            }}
                            data-testid="private-access-honeypot"
                        />

                        <div className="flex items-center gap-3 mb-3">
                            <span className="inline-flex items-center justify-center w-9 h-9 border border-[#C9A86A]/50 text-[#C9A86A]">
                                <Lock size={15} strokeWidth={1.4} />
                            </span>
                            <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                Private Portfolio
                            </span>
                        </div>
                        <h1 className="font-serif text-5xl md:text-6xl text-[#F3EFE7] leading-[1.04] mb-5">
                            By{" "}
                            <span className="italic text-[#C9A86A]">
                                Invitation.
                            </span>
                        </h1>
                        <p className="text-[#9A958A] text-[15px] leading-[1.85] mb-8 max-w-lg">
                            This area is reserved for prospective clients and
                            design partners. All visitors and content are
                            governed by an NDA.
                        </p>

                        <div className="space-y-6">
                            <AccessFormFields
                                form={form}
                                errors={errors}
                                setField={setField}
                            />

                            <NDAConsent
                                checked={form.nda_accepted}
                                error={errors.nda_accepted}
                                onChange={(e) =>
                                    setField("nda_accepted", e.target.checked)
                                }
                                onOpenNDA={() => setNdaOpen(true)}
                            />

                            {submitError && (
                                <div
                                    role="alert"
                                    className="flex items-start gap-2 border border-[#C97A6A]/40 bg-[#C97A6A]/5 px-4 py-3 text-[13px] text-[#E0A89A] leading-[1.7]"
                                    data-testid="private-access-error"
                                >
                                    <AlertCircle
                                        size={14}
                                        strokeWidth={1.6}
                                        className="mt-[2px] shrink-0"
                                    />
                                    <span>{submitError}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={status === "loading" || status === "checking"}
                                className="btn-gold w-full disabled:opacity-70 disabled:cursor-wait"
                                data-testid="private-access-submit"
                            >
                                {status === "loading" ? (
                                    <>
                                        <span className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                        Requesting
                                    </>
                                ) : (
                                    "Request Entry"
                                )}
                            </button>
                        </div>
                    </form>

                    <p className="mt-8 text-center text-[12px] text-[#9A958A] leading-[1.8] max-w-md mx-auto">
                        If you have not been issued a passcode, please contact
                        the concierge at{" "}
                        <a
                            href={BRAND.emailHref}
                            className="text-[#C9A86A] hover:text-[#D9BC80] transition-colors duration-300"
                        >
                            {BRAND.email}
                        </a>
                        .
                    </p>
                </motion.div>
            </main>

            <NDAModal open={ndaOpen} onOpenChange={setNdaOpen} />
        </div>
    );
}
